import type { Table } from '@tanstack/react-table';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import InfoTooltip from '~/components2/InfoTooltip';
import store from '../store';
import type { UtilityAgency } from '.';
import { nextEstTimeSort } from './estimatedResponseTime';

// PostHog `source` for the sort event — which surface the header lives on.
type EstTimeSortSource = 'table' | 'select_assets';

const EST_TIME_HEADER_TOOLTIP = (
  <div>
    Estimated response time – How long this agency typically takes to return records, based on CivilGrid's recent
    experience with them. It reflects time to the final deliverable, not a guarantee. A wider range means less
    certainty. Shown for records you have not requested yet; a placed request shows the date it was made instead.
  </div>
);

/**
 * Custom sortable "Est time" header shared by the utilities table and the Select Assets step, so both
 * surfaces cycle identically (est time → confidence → off via the hidden `estTimeDays` / `estTimeConfidence`
 * columns) and only ever diverge on their test ids and PostHog `source`. Returns a TanStack header renderer.
 */
export function makeEstTimeSortHeader({ testIdPrefix, source }: { testIdPrefix: string; source: EstTimeSortSource }) {
  return function EstTimeSortHeader({ table }: { table: Table<UtilityAgency> }) {
    const activeId = table.getState().sorting[0]?.id;
    const indicator = activeId === 'estTimeDays' ? '↑ time' : activeId === 'estTimeConfidence' ? '↓ confidence' : '';
    return (
      // Keep "Est time" + the info icon on the first line; the active sort mode drops to a second line so
      // the info icon is never pushed out of the narrow column.
      <div className="flex flex-col" data-testid={`${testIdPrefix}-est-time-header`}>
        <div className="flex items-center space-x-1">
          <button
            type="button"
            data-testid={`${testIdPrefix}-est-time-sort`}
            className="cursor-pointer whitespace-nowrap"
            aria-label="Sort by estimated time, then by confidence"
            onClick={() => {
              const next = nextEstTimeSort(table.getState().sorting);
              table.setSorting(next);
              const nextId = next[0]?.id;
              const sortMode =
                nextId === 'estTimeDays' ? 'time' : nextId === 'estTimeConfidence' ? 'confidence' : 'off';
              if (store.project?.id) {
                capturePosthogEvent(POSTHOG_EVENTS.utilitiesEstTimeSortChanged, {
                  project_id: store.project.id,
                  sort_mode: sortMode,
                  source,
                });
              }
            }}
          >
            Est time
          </button>
          <InfoTooltip
            text={EST_TIME_HEADER_TOOLTIP}
            label="About estimated time"
            testId={`${testIdPrefix}-est-time-info`}
          />
        </div>
        {indicator && <span className="whitespace-nowrap text-grey-60 text-xs">{indicator}</span>}
      </div>
    );
  };
}
